import {
  Box,
  Container,
  Flex,
  Heading,
  Icon,
  Text,
  useColorMode,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { Bot, Sparkles, Users } from 'lucide-react'

const MotionBox = motion.create(Box)

const fadeInUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
}

/** AI flow visual — Block 01 */
function AIFlowVisual({ isDark }: { isDark: boolean }) {
  const bg = isDark ? 'stone.900' : 'white'
  const border = isDark ? 'stone.700' : 'stone.200'
  const iconBg = isDark ? 'stone.800' : 'stone.100'
  const textColor = isDark ? 'stone.300' : 'stone.600'
  const subColor = 'stone.500'

  const steps = [
    {
      icon: Bot,
      text: 'AI generates Markdown',
      sub: 'Claude, ChatGPT, Cursor, any LLM',
      highlight: false,
    },
    {
      icon: Sparkles,
      text: 'myrkup encrypts and renders',
      sub: 'Paste, API, or MCP. Your choice',
      highlight: true,
    },
    {
      icon: Users,
      text: 'Your team gets a clean link',
      sub: 'Beautiful, private, no login needed',
      highlight: false,
    },
  ]

  return (
    <Box
      bg={bg}
      borderRadius="12px"
      border="1px solid"
      borderColor={border}
      overflow="hidden"
      w="full"
      maxW="400px"
    >
      <Flex direction="column" align="center" gap={3} p={7}>
        {steps.map((step, i) => (
          <Box key={step.text} w="full">
            {i > 0 && (
              <Text
                textAlign="center"
                color={isDark ? 'stone.600' : 'stone.400'}
                fontSize="16px"
                mb={3}
              >
                ↓
              </Text>
            )}
            <Flex
              align="center"
              gap={3}
              w="full"
              px={4}
              py={3}
              border="1px solid"
              borderColor={step.highlight ? 'brand.500' : border}
              borderRadius="8px"
              bg={step.highlight ? (isDark ? 'rgba(16,185,129,0.03)' : 'rgba(16,185,129,0.02)') : 'transparent'}
            >
              <Flex
                w="36px"
                h="36px"
                align="center"
                justify="center"
                borderRadius="8px"
                bg={step.highlight ? (isDark ? 'rgba(16,185,129,0.1)' : 'rgba(16,185,129,0.08)') : iconBg}
                flexShrink={0}
              >
                <Icon
                  as={step.icon}
                  boxSize={4}
                  color={step.highlight ? (isDark ? 'brand.400' : 'brand.600') : (isDark ? 'stone.400' : 'stone.500')}
                />
              </Flex>
              <Box>
                <Text
                  fontSize="13px"
                  fontWeight={500}
                  color={step.highlight ? (isDark ? 'brand.400' : 'brand.600') : textColor}
                >
                  {step.text}
                </Text>
                <Text fontSize="12px" color={subColor}>
                  {step.sub}
                </Text>
              </Box>
            </Flex>
          </Box>
        ))}
      </Flex>
    </Box>
  )
}

/** Encryption flow visual — Block 02 */
function EncryptionVisual({ isDark }: { isDark: boolean }) {
  const bg = isDark ? 'stone.900' : 'white'
  const border = isDark ? 'stone.700' : 'stone.200'
  const accent = isDark ? 'brand.400' : 'brand.600'

  return (
    <Box
      bg={bg}
      borderRadius="12px"
      border="1px solid"
      borderColor={border}
      p={6}
      w="full"
      maxW="400px"
    >
      <Flex direction="column" align="center" gap={4} fontFamily="mono" fontSize="13px">
        <Box textAlign="center">
          <Text color={isDark ? 'stone.300' : 'stone.600'} fontWeight={500}>Your content</Text>
          <Text color="stone.500" fontSize="12px">readable text</Text>
        </Box>
        <Text color={accent} fontSize="20px">↓</Text>
        <Box
          px={4}
          py={2}
          bg={isDark ? 'stone.800' : 'stone.100'}
          borderRadius="8px"
          textAlign="center"
        >
          <Text color={accent} fontWeight={700} fontSize="12px" textTransform="uppercase" letterSpacing="0.05em">
            AES-256-GCM
          </Text>
          <Text color="stone.500" fontSize="11px">before storage</Text>
        </Box>
        <Text color={accent} fontSize="20px">↓</Text>
        <Box textAlign="center">
          <Text color={isDark ? 'stone.300' : 'stone.600'} fontWeight={500}>Ciphertext only</Text>
          <Text color="stone.500" fontSize="12px">sent to server</Text>
        </Box>
      </Flex>
    </Box>
  )
}

/** Share controls visual — Block 03 */
function ShareControlsVisual({ isDark }: { isDark: boolean }) {
  const bg = isDark ? 'stone.900' : 'white'
  const border = isDark ? 'stone.700' : 'stone.200'
  const chipBg = isDark ? 'stone.800' : 'stone.100'
  const chipBorder = isDark ? 'stone.700' : 'stone.300'

  const expirationChips = [
    { label: '7 days', active: true },
    { label: '30 days', active: false },
    { label: 'Never', active: false },
    { label: 'Burn after read', active: false },
  ]

  return (
    <Box
      bg={bg}
      borderRadius="12px"
      border="1px solid"
      borderColor={border}
      p={6}
      w="full"
      maxW="400px"
    >
      <Flex direction="column" gap={4}>
        {/* URL bar + Copy */}
        <Flex align="center" gap={2}>
          <Box
            flex={1}
            fontFamily="mono"
            fontSize="12px"
            color={isDark ? 'brand.400' : 'brand.600'}
            bg={chipBg}
            px={3}
            py={2}
            borderRadius="8px"
            border="1px solid"
            borderColor={chipBorder}
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            myrkup.com/view/k8x2m9p
          </Box>
          <Box
            fontSize="12px"
            color={isDark ? 'stone.300' : 'stone.600'}
            bg={chipBg}
            border="1px solid"
            borderColor={chipBorder}
            px={3}
            py={2}
            borderRadius="8px"
            whiteSpace="nowrap"
          >
            Copy
          </Box>
        </Flex>

        {/* Metadata */}
        <Flex gap={4} fontSize="12px" color="stone.500">
          <Text>Expires in 7 days</Text>
          <Text>Passcode protected</Text>
        </Flex>

        {/* Expiration chips */}
        <Flex gap={2} flexWrap="wrap">
          {expirationChips.map((chip) => (
            <Box
              key={chip.label}
              fontSize="11px"
              color={chip.active ? (isDark ? 'brand.400' : 'brand.600') : (isDark ? 'stone.400' : 'stone.500')}
              bg={chip.active ? (isDark ? 'rgba(16,185,129,0.05)' : 'rgba(16,185,129,0.05)') : chipBg}
              px={3}
              py={1.5}
              borderRadius="6px"
              border="1px solid"
              borderColor={chip.active ? 'brand.500' : chipBorder}
            >
              {chip.label}
            </Box>
          ))}
        </Flex>
      </Flex>
    </Box>
  )
}

interface EditorialBlockProps {
  id?: string
  title: string
  description: string
  visual: React.ReactNode
  reverse?: boolean
  altBg?: boolean
  isDark: boolean
}

function EditorialBlock({ id, title, description, visual, reverse, altBg, isDark }: EditorialBlockProps) {
  return (
    <Box
      id={id}
      scrollMarginTop={id ? '72px' : undefined}
      py={{ base: 16, md: 24 }}
      bg={altBg ? (isDark ? 'stone.900' : 'stone.100') : (isDark ? 'transparent' : 'white')}
    >
      <Container maxW="960px">
        <MotionBox
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
          variants={fadeInUp}
        >
          <Flex
            direction={{ base: 'column', md: reverse ? 'row-reverse' : 'row' }}
            align="center"
            gap={{ base: 10, md: 16 }}
          >
            {/* Text side */}
            <Box flex={1}>
              <Heading
                as="h2"
                fontFamily="heading"
                fontWeight={700}
                fontSize={{ base: '2rem', md: 'clamp(2rem, 4.5vw, 2.75rem)' }}
                letterSpacing="-0.025em"
                lineHeight={1.15}
                color={isDark ? 'stone.50' : 'stone.900'}
                mb={4}
              >
                {title}
              </Heading>
              <Text
                fontSize="1.125rem"
                color="stone.400"
                lineHeight={1.8}
                maxW="560px"
              >
                {description}
              </Text>
            </Box>

            {/* Visual side */}
            <Flex flex={1} justify="center">
              {visual}
            </Flex>
          </Flex>
        </MotionBox>
      </Container>
    </Box>
  )
}

export function EditorialBlocks() {
  const { colorMode } = useColorMode()
  const isDark = colorMode === 'dark'

  return (
    <>
      {/* Divider */}
      <Box maxW="960px" mx="auto" h="1px" bg={isDark ? 'stone.700' : 'stone.200'} />

      <EditorialBlock
        id="how-it-works"
        title="AI generates it. myrkup shares it."
        description="Claude writes your meeting notes. ChatGPT drafts your proposal. Cursor documents your API. Now what? Let myrkup turn it into a link your team can actually read."
        visual={<AIFlowVisual isDark={isDark} />}
        altBg
        isDark={isDark}
      />

      <EditorialBlock
        id="security"
        title="Private by default."
        description="Every share is encrypted. The server only stores ciphertext. Whether content comes through the browser, MCP, or the API, only someone with the link and passcode can decrypt it."
        visual={<EncryptionVisual isDark={isDark} />}
        reverse
        isDark={isDark}
      />

      <EditorialBlock
        id="features"
        title="Share with a single link."
        description="Get an instant link. Set an expiration, add a passcode, or let it live forever. Anyone with the link sees a polished, rendered document."
        visual={<ShareControlsVisual isDark={isDark} />}
        altBg
        isDark={isDark}
      />
    </>
  )
}
